
import { apiEndpoints } from '../../amorg-common/apiEndpoints';
import { SurvyBenchmarkData } from '../../amorg-common/DTOs/survy/SurvyBenchmark';
import { Survy } from '../../amorg-common/DTOs/survy/Survy';
import { SurvyResponse } from '../../amorg-common/DTOs/survy/SurvyResponse';

export async function getSurvy(key: string, surveyId: string): Promise<Survy | null> {
    const path = apiEndpoints.survy_key_surveys_surveyId
        .replace(':key', key)
        .replace(':surveyId', surveyId);

    const response = await fetch(path, {
        method: 'GET',
        cache: 'no-cache',
        headers: { 'Content-Type': 'application/json' }
    });

    if (response.status === 404)
        return null;
    if (response.status !== 200)
        throw new Error(`Failed to load simple survey (${response.status})`);

    return await response.json() as Survy;
}

export async function getSurvyBenchmark(key: string, surveyId: string): Promise<SurvyBenchmarkData | null> {
    const path = apiEndpoints.survy_key_surveys_surveyId_benchmark
        .replace(':key', key)
        .replace(':surveyId', surveyId);

    const response = await fetch(path, {
        method: 'GET',
        cache: 'no-cache',
        headers: { 'Content-Type': 'application/json' }
    });

    if (response.status === 404)
        return null;
    if (response.status !== 200)
        throw new Error(`Failed to load simple survey benchmark (${response.status})`);

    return await response.json() as SurvyBenchmarkData;
}

export async function createSurvyResponse(key: string, responseId: string, surveyId: string): Promise<'created' | 'exists'> {
    const path = apiEndpoints.survy_key_responses_responseId
        .replace(':key', key)
        .replace(':responseId', responseId);

    const response = await fetch(path, {
        method: 'POST',
        cache: 'no-cache',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ surveyId })
    });

    if (response.status === 201)
        return 'created';
    if (response.status === 409)
        return 'exists';

    throw new Error(`Failed to create simple survey response (${response.status})`);
}

export async function getSurvyResponse(key: string, responseId: string): Promise<SurvyResponse | null> {
    const path = apiEndpoints.survy_key_responses_responseId
        .replace(':key', key)
        .replace(':responseId', responseId);

    const response = await fetch(path, {
        method: 'GET',
        cache: 'no-cache',
        headers: { 'Content-Type': 'application/json' }
    });

    if (response.status === 404)
        return null;
    if (response.status !== 200)
        throw new Error(`Failed to load simple survey response (${response.status})`);

    const payload = await response.json() as Omit<SurvyResponse, 'created' | 'lastUpdated' | 'finalized'> & {
        created: string;
        lastUpdated: string;
        finalized?: string | null;
    };

    return {
        ...payload,
        created: new Date(payload.created),
        lastUpdated: new Date(payload.lastUpdated),
        finalized: payload.finalized ? new Date(payload.finalized) : null
    };
}

export async function submitSurvyAnswer(
    key: string,
    responseId: string,
    questionIndex: number,
    answerValue: string
): Promise<void> {
    const path = apiEndpoints.survy_key_responses_responseId_questions_questionIndex_answerValue
        .replace(':key', key)
        .replace(':responseId', responseId)
        .replace(':questionIndex', `${questionIndex}`)
        .replace(':answerValue', encodeURIComponent(answerValue));

    const response = await fetch(path, {
        method: 'PUT',
        cache: 'no-cache',
        headers: { 'Content-Type': 'application/json' }
    });

    if (response.status !== 200)
        throw new Error(`Failed to submit simple survey answer (${response.status})`);
}


export async function finalizeSurvyResponse(
    key: string,
    responseId: string,
    respondentDetails: SurvyResponse['respondentDetails']
): Promise<void> {
    const path = apiEndpoints.survy_key_responses_responseId_finalize
        .replace(':key', key)
        .replace(':responseId', responseId);

    const response = await fetch(path, {
        method: 'PUT',
        cache: 'no-cache',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ respondentDetails })
    });

    if (response.status !== 200)
        throw new Error(`Failed to finalize simple survey response (${response.status})`);
}

export async function reopenSurvyResponse(
    key: string,
    responseId: string
): Promise<void> {
    const path = apiEndpoints.survy_key_responses_responseId_reopen
        .replace(':key', key)
        .replace(':responseId', responseId);

    const response = await fetch(path, {
        method: 'PUT',
        cache: 'no-cache',
        headers: { 'Content-Type': 'application/json' }
    });

    if (response.status !== 200)
        throw new Error(`Failed to reopen simple survey response (${response.status})`);
}
